import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import styles from "./FullCalendarPage.module.css";
import { ComplianceCalendar } from "../../../Services/APIrequests";
import Nav from "../Nav/Nav";
// Assuming Nav is in ../Nav/Nav relative to LandingPage/HomePage. 
// Actually FullCalendarPage is in src/Components/LandingPage/HomePage/
// So ../../Nav/Nav is correct.

const FullCalendarPage = () => {
    const navigate = useNavigate();
    const [value, onChange] = useState(new Date()); // Selected date
    const [activeStartDate, setActiveStartDate] = useState(new Date()); // Visible month
    const [events, setEvents] = useState([]);
    const [displayedEvents, setDisplayedEvents] = useState([]);

    // Fetch events when month changes
    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const startMonth = moment(activeStartDate).startOf("month").format("YYYY-MM");
                const endMonth = moment(activeStartDate).endOf("month").format("YYYY-MM");

                // Fetch a bit more to handle month transitions if needed, but API takes start/end month
                const response = await ComplianceCalendar(startMonth, endMonth);

                if (response.data && response.data.entries) {
                    const formattedEvents = response.data.entries.map(evt => ({
                        ...evt,
                        dateObj: new Date(evt.date)
                    }));
                    setEvents(formattedEvents);
                } else {
                    setEvents([]);
                }
            } catch (error) {
                console.error("Error fetching calendar events:", error);
            }
        };

        fetchEvents();
    }, [activeStartDate]);

    // Update displayed list based on selection or view
    useEffect(() => {
        // Logic: If a specific date is selected (value), show events for that date?
        // Or show events for the whole month? 
        // Image shows "All Deadlines - February". Implicitly shows all for the month.
        // But selecting a date usually filters.
        // Let's filter by month primarily.

        const startOfMonth = moment(activeStartDate).startOf('month');
        const endOfMonth = moment(activeStartDate).endOf('month');

        const monthEvents = events.filter(evt =>
            moment(evt.dateObj).isBetween(startOfMonth, endOfMonth, 'day', '[]')
        );

        // Sort by date
        monthEvents.sort((a, b) => a.dateObj - b.dateObj);

        setDisplayedEvents(monthEvents);

    }, [events, activeStartDate]);

    const handleActiveStartDateChange = ({ activeStartDate }) => {
        setActiveStartDate(activeStartDate);
    };

    const getTagStyle = (description) => {
        const lower = description?.toLowerCase() || "";
        if (lower.includes("gst")) return { bg: "#d1fae5", color: "#059669" }; // Green for GST
        if (lower.includes("income")) return { bg: "#dbeafe", color: "#2563eb" }; // Blue for Income Tax
        if (lower.includes("labour")) return { bg: "#ffedd5", color: "#ea580c" }; // Orange for Labour Law
        return { bg: "#f3f4f6", color: "#4b5563" };
    };

    // Custom tile content to show dots
    const tileContent = ({ date, view }) => {
        if (view === 'month') {
            const hasEvent = events.some(evt => moment(evt.dateObj).isSame(date, 'day'));
            if (hasEvent) {
                return <div className={styles.dot}></div>;
            }
        }
        return null;
    };

    return (
        <div className={styles.pageContainer}>
            <Nav /> {/* Optional: Include Nav if it's a full page within the layout */}

            <div className={styles.contentWrapper}>
                <div className={styles.header}>
                    <button onClick={() => navigate("/")} className={styles.backButton}>
                        <FaArrowLeft /> Back to Home
                    </button>
                    {/* <span className={styles.brandName}>KOHere</span> */}
                </div>

                <div className={styles.pageTitleSection}>
                    <h1 className={styles.pageTitle}>Compliance Calendar</h1>
                    <p className={styles.pageSubtitle}>Track all your upcoming compliance deadlines in one place.</p>
                </div>

                <div className={styles.calendarLayout}>
                    {/* Left Column: Calendar */}
                    <div className={styles.calendarCard}>
                        <Calendar
                            onChange={onChange}
                            value={value}
                            onActiveStartDateChange={handleActiveStartDateChange}
                            tileContent={tileContent}
                            className={styles.reactCalendar}
                            prev2Label={null} // Hide double arrows if desired for cleaner look
                            next2Label={null}
                        />
                    </div>

                    {/* Right Column: Events List */}
                    <div className={styles.eventsCard}>
                        <h3 className={styles.eventsHeader}>
                            All Deadlines – {moment(activeStartDate).format("MMMM")}
                        </h3>

                        <div className={styles.eventsList}>
                            {displayedEvents.length > 0 ? displayedEvents.map((evt, idx) => {
                                // Use categoryName first for tag styling check logic if available, else description/title
                                const tagText = evt.categoryName || evt.description || "Compliance";
                                const tagStyle = getTagStyle(tagText);
                                return (
                                    <div key={idx} className={styles.eventItem}>
                                        <div className={styles.eventInfo}>
                                            <h4 className={styles.eventTitle}>{evt.title}</h4>
                                            <span className={styles.eventTag} style={{ backgroundColor: tagStyle.bg, color: tagStyle.color }}>
                                                {tagText}
                                            </span>
                                        </div>
                                        <div className={styles.eventDate}>
                                            {moment(evt.dateObj).format("MMM D")}
                                        </div>
                                    </div>
                                );
                            }) : (
                                <p className={styles.noEvents}>No deadlines found for this month.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FullCalendarPage;
